import initFilteringOptions from './initFilteringOptions';
import initStickyFilteringMenu from './stickyFilteringMenu';
import { addFilterToURL, removeFilterFromURL } from './filterURL';
import { selectNewFilter } from '../../sections/collection/filteredCollection';
import AppliedFiltersList from './AppliedFiltersList';
import Sorting from './sorting';
import initFilterMenuADA from './filterMenuADA';
import { initMobileFilters, openMobileFilterNav } from './mobileFilters';

// Called from sections/collection/filteredCollection.js
export default class FilteringSorting {
  elements;
  from_search;
  prefix;
  appliedFiltersListInstance;
  sortingInstance;
  optionFocusabilityObserver;
  lastAllFiltersTouchTimestamp = 0;
  // filterInstance;

  constructor(from_search = false) {
    this.from_search = from_search;

    this.prefix = (this.from_search) ? 'overlay-search-' : '';

    this.elements = {
      allFiltersButton: document.querySelectorAll(`[data-${this.prefix}show-all-filters]`),
      // allSortingButton: document.querySelectorAll(`[data-${this.prefix}show-all-sorting]`),
      // individualFilterButtons: document.querySelectorAll(`[data-${this.prefix}show-individual-filter]`),
    };

    if (!this.from_search) {
      initFilteringOptions();
      initStickyFilteringMenu();
    }

    this.sortingInstance = new Sorting(this.from_search);
    initFilterMenuADA(this.from_search);
    initMobileFilters(this.from_search);
    this.ensureOptionRolesAreFocusable();
    this.observeOptionRoleFocusability();
    this.initListeners();
    this.appliedFiltersListInstance = new AppliedFiltersList(this.from_search);
  }

  ensureOptionRolesAreFocusable = () => {
    document.querySelectorAll(`[data-${this.prefix}filter-options-items] [role="option"]`).forEach((optionEl) => {
      if (!optionEl.hasAttribute('tabindex')) {
        optionEl.setAttribute('tabindex', '0');
      }
    });
  };

  observeOptionRoleFocusability = () => {
    if (this.optionFocusabilityObserver || typeof MutationObserver === 'undefined') {
      return;
    }

    this.optionFocusabilityObserver = new MutationObserver(() => {
      this.ensureOptionRolesAreFocusable();
    });

    this.optionFocusabilityObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['role'],
    });
  };

  /**
   * Initialize event listeners for the filtering and sorting menu
   * 
   * @returns {void}
   */
  initListeners = () => {
    this.elements.allFiltersButton.forEach((button) => {
      button.addEventListener('touchend', (e) => {
        this.lastAllFiltersTouchTimestamp = Date.now();
        e.preventDefault();
        openMobileFilterNav();
      });

      button.addEventListener('click', (e) => {
        if (Date.now() - this.lastAllFiltersTouchTimestamp < 500) {
          return;
        }

        e.preventDefault();
        openMobileFilterNav();
      });
    });

    document.addEventListener('click', async (e) => {
      const clickedInsideFilterOptions = e.target.closest(`[data-${this.prefix}filter-options-container]`);
      if (clickedInsideFilterOptions) {
        // Click a filter option
        const optionTarget = e.target.closest(`[data-${this.prefix}filter-options-items] [data-clickable-option]`);
        if (optionTarget) {
          await this.selectFilterOption(optionTarget);
          return;
        }

        // Clicking the container itself should open/keep-open its associated filter parent.
        const filterParent = clickedInsideFilterOptions.closest(`[data-${this.prefix}show-individual-filter]`);
        if (filterParent) {
          this.selectFilterParent(filterParent);
        }

        return;
      }

      // We click outside the filter or sorting options
      const filter_target = e.target.closest(`[data-${this.prefix}show-individual-filter]`);
      const sorting_target = e.target.closest(`[data-${this.prefix}show-desktop-sort-nav]`);
      if (!filter_target && !sorting_target) {
        this.clearActiveFilterParents();
        return;
      }

      // Click the filter parent button
      this.selectFilterParent(filter_target);
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' || e.keyCode === 27) {
        this.clearActiveFilterParents();
        return;
      }

      const optionTarget = e.target.closest(`[data-${this.prefix}filter-options-items] [data-clickable-option]`);
      if (optionTarget) {
        return;
      }

      const filterTarget = e.target.closest(`[data-${this.prefix}show-individual-filter]`);
      if (filterTarget && window.theme.breakpoints.current !== 'mobile') {
        const isEnter = e.key === 'Enter' || e.keyCode === 13;
        const isSpace = e.key === ' ' || e.key === 'Spacebar' || e.keyCode === 32;
        const isArrowDown = e.key === 'ArrowDown' || e.keyCode === 40;
        const isArrowUp = e.key === 'ArrowUp' || e.keyCode === 38;
        const isArrowRight = e.key === 'ArrowRight' || e.keyCode === 39;
        const isArrowLeft = e.key === 'ArrowLeft' || e.keyCode === 37;
        const isHome = e.key === 'Home' || e.keyCode === 36;
        const isEnd = e.key === 'End' || e.keyCode === 35;

        if (isArrowRight || isArrowLeft || isHome || isEnd) {
          e.preventDefault();

          if (isHome) {
            this.focusFilterParentByIndex(0);
            return;
          }

          if (isEnd) {
            this.focusFilterParentByIndex(-1);
            return;
          }

          this.focusAdjacentFilterParent(filterTarget, isArrowRight ? 1 : -1);
          return;
        }

        if (isEnter || isSpace || isArrowDown) {
          e.preventDefault();
          const wasActive = filterTarget.classList.contains('active');
          this.selectFilterParent(filterTarget);

          const isNowActive = filterTarget.classList.contains('active');
          if (!wasActive && isNowActive) {
            this.focusFirstFilterOption(filterTarget);
          }
          return;
        }

        if (isArrowUp) {
          e.preventDefault();
          const wasActive = filterTarget.classList.contains('active');
          this.selectFilterParent(filterTarget);

          const isNowActive = filterTarget.classList.contains('active');
          if (!wasActive && isNowActive) {
            this.focusLastFilterOption(filterTarget);
          }
          return;
        }
      }

    });
  };

  getFilterParentButtons = () => {
    const filterBy = document.querySelector(`[data-${this.prefix}filter-by]`);
    if (!filterBy) {
      return [];
    }

    return Array.from(filterBy.querySelectorAll(`[data-${this.prefix}show-individual-filter]`));
  };

  focusFilterParentByIndex = (index) => {
    const filterParents = this.getFilterParentButtons();
    if (!filterParents.length) {
      return;
    }

    const normalizedIndex = index < 0 ? filterParents.length - 1 : index;
    const target = filterParents[normalizedIndex];
    if (target instanceof HTMLElement) {
      target.focus();
    }
  };

  focusAdjacentFilterParent = (currentFilterTarget, direction = 1) => {
    const filterParents = this.getFilterParentButtons();
    if (!filterParents.length) {
      return;
    }

    const currentIndex = filterParents.indexOf(currentFilterTarget);
    if (currentIndex === -1) {
      return;
    }

    const nextIndex = currentIndex + direction;
    if (nextIndex < 0 || nextIndex >= filterParents.length) {
      return;
    }

    const target = filterParents[nextIndex];
    if (target instanceof HTMLElement) {
      target.focus();
    }
  };

  /**
   * Move keyboard focus to the first selectable option within a desktop filter panel
   *
   * @param {HTMLElement} filterTarget
   * @returns {void}
   */
  focusFirstFilterOption = (filterTarget) => {
    const firstOption = filterTarget.querySelector(`[data-${this.prefix}filter-options-items] [data-clickable-option]`);
    if (firstOption instanceof HTMLElement) {
      firstOption.focus();
    }
  };

  /**
   * Move keyboard focus to the last selectable option within a desktop filter panel
   *
   * @param {HTMLElement} filterTarget
   * @returns {void}
   */
  focusLastFilterOption = (filterTarget) => {
    const options = filterTarget.querySelectorAll(`[data-${this.prefix}filter-options-items] [data-clickable-option]`);
    const lastOption = options[options.length - 1];
    if (lastOption instanceof HTMLElement) {
      lastOption.focus();
    }
  };

  /**
   * Clear the "active" class from all filter parent buttons
   * 
   * @returns {void}
   */
  clearActiveFilterParents = () => {
    const allFilterButtons = document.querySelectorAll(`[data-${this.prefix}show-individual-filter]`);
    allFilterButtons.forEach((button) => {
      button.classList.remove('active');
      button.setAttribute('aria-expanded', 'false');

      const panel = button.querySelector(`[data-${this.prefix}filter-options-container]`);
      if (panel) {
        panel.setAttribute('aria-hidden', 'true');
        panel.setAttribute('tabindex', '-1');
      }
    });

    this.sortingInstance.closeSortingSubnav();
  };

  /**
   * Select a filter parent (the button that opens the filter options)
   * 
   * @param {HTMLElement} target
   * @returns {void}
   */
  selectFilterParent = (target) => {
    if (!target) {
      return;
    }

    // We don't open the filter menu options on mobile, we open the drawer instead
    if (window.theme.breakpoints.current === 'mobile') {
      openMobileFilterNav(target);
      return;
    }

    const isAlreadyActive = target.classList.contains('active');

    if (isAlreadyActive) {
      target.classList.remove('active');
      target.setAttribute('aria-expanded', 'false');

      const panel = target.querySelector(`[data-${this.prefix}filter-options-container]`);
      if (panel) {
        panel.setAttribute('aria-hidden', 'true');
        panel.setAttribute('tabindex', '-1');
      }
      return;
    }

    this.clearActiveFilterParents();
    target.classList.add('active');
    target.setAttribute('aria-expanded', 'true');

    const panel = target.querySelector(`[data-${this.prefix}filter-options-container]`);
    if (panel) {
      panel.setAttribute('aria-hidden', 'false');
      panel.setAttribute('tabindex', '0');
    }
  };

  /**
   * Select a filter option from a filter menu
   * 
   * @param {HTMLElement} target
   * @returns {Promise<void>}
   */
  selectFilterOption = async (target) => {
    const option_type = target.closest(`[data-${this.prefix}filter-options]`);
    const option_value = target.getAttribute('data-option-value');
    const option_short_value = target.getAttribute(`data-option-short-value`);

    // Sync desktop/mobile selections
    const all_same_items = document.querySelectorAll(
      `[data-${this.prefix}filter-options-type="${option_type.getAttribute(`data-${this.prefix}filter-options-type`)}"] [data-option-value="${option_value}"]`
    );
    all_same_items.forEach(all_same_item => {
      all_same_item.classList.toggle('active');
      all_same_item.setAttribute('aria-checked', String(all_same_item.classList.contains('active')));

      const adaInput = all_same_item.querySelector('[data-option-ada]');
      if (adaInput) {
        adaInput.checked = all_same_item.classList.contains('active');
      }

      if (all_same_item.parentElement) {
        all_same_item.parentElement.classList.toggle('active');
      }
    });

    // Select/unselect all similar options
    if (target.getAttribute('data-master-option') === 'true') {
      const all_similar_options = document.querySelectorAll(
        `[data-${this.prefix}filter-options-type="${option_type.getAttribute(`data-${this.prefix}filter-options-type`)}"] [data-option-short-value="${option_short_value}"]`
      );
      all_similar_options.forEach(all_similar_option => {
        if (all_similar_option.getAttribute('data-master-option') !== 'true') {
          all_similar_option.classList.toggle('active');
          all_similar_option.setAttribute('aria-checked', String(all_similar_option.classList.contains('active')));

          const adaInput = all_similar_option.querySelector('[data-option-ada]');
          if (adaInput) {
            adaInput.checked = all_similar_option.classList.contains('active');
          }

          if (all_similar_option.parentElement) {
            all_similar_option.parentElement.classList.toggle('active');
          }
        }
      });
    }

    this.appliedFiltersListInstance.updateFiltersList(target);
    this.updateFiltersURL(target);
    this.updateFilterCount(target);
    await selectNewFilter();
  };

  /**
   * Update the count of selected filters displayed in the UI
   * 
   * @returns {void}
   */
  updateFilterCount = (target_or_filter) => {
    const filter_selector = `[data-${this.prefix}filter-options]`;
    const is_filter_container = target_or_filter?.matches?.(filter_selector);
    const filter = is_filter_container ? target_or_filter : target_or_filter?.closest(filter_selector);
    if (!filter) {
      return;
    }

    const filterCountElement = filter.querySelector(`[data-${this.prefix}filter-count]`);
    if (!filterCountElement) {
      return;
    }

    const selectedOptions = filter.querySelectorAll(`[data-${this.prefix}clickable-option-li].active`);
    const selectedCount = selectedOptions.length;

    if (selectedCount > 0) {
      filterCountElement.textContent = `(${selectedCount})`;
      filterCountElement.classList.add('active');
    } else {
      filterCountElement.textContent = '';
      filterCountElement.classList.remove('active');
    }
  };

  /**
   * Refresh all filter counters based on currently active options
   * 
   * @returns {void}
   */
  updateAllFilterCounts = () => {
    document.querySelectorAll(`[data-${this.prefix}filter-options]`).forEach((filter) => {
      this.updateFilterCount(filter);
    });
  };

  /**
   * Update the URL to reflect the selected filters
   * 
   * @param {HTMLElement} target 
   * @returns {void}
   */
  updateFiltersURL = (target) => {
    if (this.from_search) {
      return;
    }

    const option_type = target.closest(`[data-${this.prefix}filter-options]`);
    const option_type_name = option_type.getAttribute(`data-${this.prefix}filter-options-type`);
    const option_type_value = target.getAttribute('data-option-value');

    (target.classList.contains('active')) ? addFilterToURL(option_type_name, option_type_value) : removeFilterFromURL(option_type_name, option_type_value);
  };
}
